import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { auditoriaAPI } from '../services/api';
import { APP_VERSION } from '../version';

const CATEGORIAS = [
  { id: 'nuevas', label: 'Nuevas funcionalidades' },
  { id: 'funcionales', label: 'Cambios funcionales' },
  { id: 'esteticos', label: 'Cambios estéticos' },
  { id: 'correcciones', label: 'Correcciones' },
];

/** YYYY-MM-DD → DD/MM/YYYY sin pasar por Date, para no correr el día por zona horaria. */
function formatFechaVersion(fecha) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fecha || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : fecha || '';
}

function contarPorCategoria(versiones) {
  const conteo = Object.fromEntries(CATEGORIAS.map((c) => [c.id, 0]));
  for (const v of versiones) {
    for (const c of CATEGORIAS) conteo[c.id] += v.novedades?.[c.id]?.length || 0;
  }
  return conteo;
}

/**
 * @param {{ onVersionActual?: (version: string) => void }} props
 */
const AuditoriaNovedades = ({ onVersionActual }) => {
  const [versiones, setVersiones] = useState([]);
  const [versionActual, setVersionActual] = useState(APP_VERSION);
  const [verDesde, setVerDesde] = useState('');
  const [verHasta, setVerHasta] = useState(APP_VERSION);
  const [cambios, setCambios] = useState([]);
  const [rango, setRango] = useState({ desde: null, hasta: APP_VERSION });
  const [loading, setLoading] = useState(false);
  const [categoria, setCategoria] = useState('');

  const actualizarVersionActual = useCallback(
    (v) => {
      if (!v) return;
      setVersionActual(v);
      onVersionActual?.(v);
    },
    [onVersionActual]
  );

  const cargarLista = useCallback(async () => {
    try {
      const res = await auditoriaAPI.getVersiones();
      if (res.success && res.data) {
        const lista = res.data.entradas || [];
        const actual = res.data.versionActual || APP_VERSION;
        setVersiones(lista);
        actualizarVersionActual(actual);
        setVerHasta(actual);
        const idx = lista.findIndex((v) => v.version === actual);
        setVerDesde(idx > 0 ? lista[idx - 1].version : '');
      }
    } catch (e) {
      toast.error('Error al cargar el historial de versiones');
    }
  }, [actualizarVersionActual]);

  const cargarCambios = useCallback(
    async (desde, hasta) => {
      try {
        setLoading(true);
        const res = await auditoriaAPI.getCambiosVersiones(desde, hasta);
        if (res.success && res.data) {
          setCambios([...(res.data.cambios || [])].reverse());
          setRango({ desde: res.data.desde, hasta: res.data.hasta });
          actualizarVersionActual(res.data.versionActual);
        }
      } catch (error) {
        toast.error(error.message || 'Error al cargar las novedades');
        setCambios([]);
      } finally {
        setLoading(false);
      }
    },
    [actualizarVersionActual]
  );

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  useEffect(() => {
    if (!verHasta) return;
    if (verDesde && verDesde === verHasta) return;
    cargarCambios(verDesde || undefined, verHasta);
  }, [verDesde, verHasta, cargarCambios]);

  const conteo = useMemo(() => contarPorCategoria(cambios), [cambios]);

  const visibles = useMemo(() => {
    if (!categoria) return cambios;
    return cambios.filter((v) => (v.novedades?.[categoria]?.length || 0) > 0);
  }, [cambios, categoria]);

  const verTodas = () => setVerDesde('');
  const ultimaActualizacion = () => {
    const idx = versiones.findIndex((v) => v.version === versionActual);
    setVerHasta(versionActual);
    setVerDesde(idx > 0 ? versiones[idx - 1].version : '');
  };

  const categoriasAMostrar = categoria ? CATEGORIAS.filter((c) => c.id === categoria) : CATEGORIAS;

  return (
    <div className="auditoria-versiones">
      <p className="versiones-ayuda">
        Qué trajo cada versión del sistema: funciones nuevas, cambios en cómo se usa, cambios visuales y correcciones.
      </p>

      <div className="auditoria-filtros versiones-filtros">
        <div className="filtro-group">
          <label htmlFor="nov-desde">Desde versión (excluida)</label>
          <select id="nov-desde" value={verDesde} onChange={(e) => setVerDesde(e.target.value)}>
            <option value="">— Todas —</option>
            {versiones.map((v) => (
              <option key={v.version} value={v.version}>
                {v.version} ({formatFechaVersion(v.fecha)})
              </option>
            ))}
          </select>
        </div>
        <div className="filtro-group">
          <label htmlFor="nov-hasta">Hasta versión (incluida)</label>
          <select id="nov-hasta" value={verHasta} onChange={(e) => setVerHasta(e.target.value)}>
            {versiones.map((v) => (
              <option key={v.version} value={v.version}>
                {v.version} ({formatFechaVersion(v.fecha)})
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="btn-secondary" onClick={ultimaActualizacion}>
          Última actualización
        </button>
        <button type="button" className="btn-secondary" onClick={verTodas}>
          Ver todas
        </button>
      </div>

      <div className="novedades-categorias" role="group" aria-label="Filtrar por categoría">
        <button
          type="button"
          className={`novedades-chip${categoria === '' ? ' active' : ''}`}
          aria-pressed={categoria === ''}
          onClick={() => setCategoria('')}
        >
          Todas
        </button>
        {CATEGORIAS.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`novedades-chip novedades-chip--${c.id}${categoria === c.id ? ' active' : ''}`}
            aria-pressed={categoria === c.id}
            onClick={() => setCategoria((prev) => (prev === c.id ? '' : c.id))}
          >
            {c.label} <span className="novedades-chip-count">{conteo[c.id]}</span>
          </button>
        ))}
      </div>

      {rango.hasta && (
        <p className="versiones-rango">
          {rango.desde ? (
            <>
              Novedades posteriores a <strong>{rango.desde}</strong> hasta <strong>{rango.hasta}</strong>
            </>
          ) : (
            <>
              Todas las versiones hasta <strong>{rango.hasta}</strong>
            </>
          )}{' '}
          ({visibles.length} {visibles.length === 1 ? 'versión' : 'versiones'})
        </p>
      )}

      {loading ? (
        <div className="loading">Cargando novedades...</div>
      ) : (
        <div className="novedades-lista">
          {visibles.length === 0 ? (
            <p className="no-data">No hay novedades en el rango seleccionado</p>
          ) : (
            visibles.map((v) => {
              const nov = v.novedades;
              return (
                <article
                  key={v.version}
                  className={`novedades-version${v.version === versionActual ? ' novedades-version--actual' : ''}`}
                >
                  <header className="novedades-version-header">
                    <h2>
                      Versión {v.version}
                      {v.version === versionActual && <span className="badge-actual">actual</span>}
                    </h2>
                    <span className="novedades-fecha">{formatFechaVersion(v.fecha)}</span>
                  </header>

                  {nov ? (
                    <>
                      {categoriasAMostrar.map((c) =>
                        nov[c.id]?.length ? (
                          <section key={c.id} className={`novedades-seccion novedades-seccion--${c.id}`}>
                            <h3>{c.label}</h3>
                            <ul>
                              {nov[c.id].map((texto) => (
                                <li key={texto}>{texto}</li>
                              ))}
                            </ul>
                          </section>
                        ) : null
                      )}
                      {!categoria && nov.nota && <p className="novedades-nota">{nov.nota}</p>}
                    </>
                  ) : (
                    <p className="novedades-nota">{v.descripcion}</p>
                  )}

                  {nov && v.descripcion && (
                    <details className="novedades-tecnico">
                      <summary>Detalle técnico</summary>
                      <p>
                        <span className="novedades-tipo" title="Tipo de versión: A base de datos, B backend y frontend, C solo frontend">
                          {v.tipo}
                        </span>{' '}
                        {v.descripcion}
                      </p>
                    </details>
                  )}
                </article>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AuditoriaNovedades;
